<!-- LAST GASP — sixty seconds of true or false.

     THE RULE THAT MAKES IT THIS MODE: a wrong answer costs SECONDS, not lives.
     Losing is running out of puff, never being told off, and that is the whole
     tone of the app in one mechanic.

     Swipe left or right as the brief asks, with two big buttons doing the same
     job. The buttons are not a fallback for the swipe — they are the primary
     control, because a swipe cannot be reached by a screen reader, by switch
     control, or by anyone playing one-handed on a bus. -->
<template>
  <IonPage>
    <AppHeader title="Last Gasp" back="/tabs/play" />

    <IonContent :fullscreen="true">
      <template v-if="!started">
        <div class="pad" style="padding-top: var(--sp-6)">
          <p class="ex-display lg">Last Gasp</p>
          <p class="ex-sub">
            Sixty seconds. True or false. Every wrong answer takes
            <strong>five seconds</strong> off the clock — so the clock is the
            only thing that can beat you.
          </p>
          <p class="ex-sub">Swipe right for TRUE, left for FALSE. Or just tap.</p>
        </div>
        <ModeRecord mode="last-gasp" />

        <div class="pad" style="margin-top: var(--sp-5)">
          <IonButton expand="block" size="large" @click="begin">Start the clock</IonButton>
        </div>
      </template>

      <template v-else>
        <GameHud :score="score" :streak="streak" label="Left" :value="seconds" />
        <div class="clock-bar" :class="clockClass" aria-hidden="true">
          <i :style="{ width: clockPct + '%' }" />
        </div>

        <div
          v-if="current"
          ref="stageRef"
          tabindex="-1"
          class="q-stage"
          :class="{ shake: shaking }"
          @pointerdown="onDown"
          @pointerup="onUp"
          @pointercancel="onCancel"
        >
          <span class="q-topic">
            <span aria-hidden="true">{{ topicEmoji(current.topic) }}</span>
            {{ topicLabel(current.topic) }}
          </span>

          <p class="q-prompt">{{ current.prompt }}</p>

          <div class="answers two-up">
            <button
              type="button"
              class="answer big"
              :class="btnClass(true)"
              :disabled="revealed || counting"
              @click="answer(true)"
            >
              TRUE
              <span v-if="revealed && btnClass(true) === 'right'" class="sr-only"> — correct answer</span>
              <span v-else-if="revealed && btnClass(true) === 'wrong'" class="sr-only"> — your answer, incorrect</span>
            </button>
            <button
              type="button"
              class="answer big"
              :class="btnClass(false)"
              :disabled="revealed || counting"
              @click="answer(false)"
            >
              FALSE
              <span v-if="revealed && btnClass(false) === 'right'" class="sr-only"> — correct answer</span>
              <span v-else-if="revealed && btnClass(false) === 'wrong'" class="sr-only"> — your answer, incorrect</span>
            </button>
          </div>
        </div>
      </template>

      <div class="tail" />
    </IonContent>

    <StartCountdown :when="counting" label="Sixty seconds" @go="runIt" />
    <VerdictFlash :text="verdict.text" :why="verdict.why" :good="verdict.good" />
    <AusBonus :when="ausNow" @done="ausNow = false" />
  </IonPage>
</template>

<script setup>
import { ref, computed, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { IonPage, IonContent, IonButton } from '@ionic/vue';
import { AppHeader, announce } from '../core/ui';
import GameHud from '../components/GameHud.vue';
import ModeRecord from '../components/ModeRecord.vue';
import VerdictFlash from '../components/VerdictFlash.vue';
import AusBonus from '../components/AusBonus.vue';
import StartCountdown from '../components/StartCountdown.vue';
import { topicEmoji, topicLabel } from '../game/topics.js';
import { drawQuestions, pointsFor, isAusAnswer } from '../game/engine.js';
import { drawOptions } from '../game/profile.js';
import { useCountdown } from '../game/clock.js';
import { finishRun } from '../game/run.js';
import { useAbandonGuard, useStopOnLeave, useResetOnEnter } from '../game/guard.js';
import { pun } from '../game/puns.js';
import { haptic } from '../core/util.js';

const router = useRouter();

const ROUND_MS = 60000;
const PENALTY_MS = 5000;

const started = ref(false);
const counting = ref(false);
const over = ref(false);
const queue = ref([]);
const idx = ref(0);
const revealed = ref(false);
const chosen = ref(null);
const shaking = ref(false);
const ausNow = ref(false);
const stageRef = ref(null);

const score = ref(0);
const streak = ref(0);
const bestChain = ref(0);
const correct = ref(0);
const wrong = ref(0);
const ausCount = ref(0);

const verdict = ref({ text: '', why: '', good: true });

const clock = useCountdown(ROUND_MS, () => end());

const current = computed(() => queue.value[idx.value] ?? null);
const seconds = computed(() => Math.ceil(clock.remaining.value / 1000));
const clockPct = computed(() => (clock.remaining.value / ROUND_MS) * 100);
const clockClass = computed(() => {
  const s = seconds.value;
  return s <= 10 ? 'panic' : s <= 25 ? 'warn' : '';
});

// A run is live from the moment the clock starts until the results are in.
useAbandonGuard(
  computed(() => started.value && !over.value),
  {
    message: 'The clock is still running. Leaving now loses this score.',
    // Confirming used only to PERMIT the navigation — the run carried on in
    // the background. "Give up" now actually gives up.
    onAbandon: () => teardown(),
  },
);

let advanceTimer = null;

function begin() {
  queue.value = drawQuestions('last-gasp', { ...drawOptions.value, count: 80 });
  started.value = true;
  // The question renders behind the countdown so it is already on screen when
  // the clock starts — he gets the full sixty seconds to answer, not fifty-nine
  // and a page paint.
  counting.value = true;
}

function runIt() {
  counting.value = false;
  clock.reset(ROUND_MS);
  clock.start();
  // Nothing else moves focus onto the board — the Start button and the
  // countdown overlay are both gone by now, and without this a keyboard or
  // screen-reader user is left on <body> for the whole round.
  nextTick(() => stageRef.value?.focus());
}

function btnClass(value) {
  if (!revealed.value || !current.value) return '';
  if (value === current.value.answer) return 'right';
  if (value === chosen.value) return 'wrong';
  return 'muted';
}

function answer(value) {
  if (revealed.value || !current.value || over.value || counting.value) return;
  const q = current.value;
  const right = value === q.answer;
  revealed.value = true;
  chosen.value = value;

  if (right) {
    const aus = isAusAnswer(q, q.prompt);
    if (aus) {
      ausCount.value += 1;
      ausNow.value = true;
    }
    const gained = pointsFor({ difficulty: q.difficulty, streak: streak.value, aus });
    score.value += gained;
    streak.value += 1;
    bestChain.value = Math.max(bestChain.value, streak.value);
    correct.value += 1;
    haptic('success');
    verdict.value = { text: streak.value >= 3 ? pun('streak') : pun('correct'), why: '', good: true };
  } else {
    streak.value = 0;
    wrong.value += 1;
    haptic('error');
    shaking.value = true;
    setTimeout(() => { shaking.value = false; }, 280);
    // The penalty IS the punishment. No lives lost, no round ended.
    clock.adjust(-PENALTY_MS);
    verdict.value = { text: pun('wrong'), why: q.reason, good: false };
  }

  // A right answer moves on almost immediately; a wrong one holds long enough
  // to read why. Getting it right should never cost reading time in a mode
  // whose entire premise is that the clock is running.
  clearTimeout(advanceTimer);
  advanceTimer = setTimeout(next, right ? 420 : 1500);
}

function next() {
  verdict.value = { text: '', why: '', good: true };
  revealed.value = false;
  chosen.value = null;
  idx.value += 1;
  if (idx.value >= queue.value.length) {
    queue.value = [...queue.value, ...drawQuestions('last-gasp', { ...drawOptions.value, count: 40 })];
  }
  // Terse on purpose: this mode can move in under half a second on a streak,
  // and a screen reader has no other way to know a new prompt has landed.
  if (current.value) announce(current.value.prompt);
}

async function end() {
  if (over.value) return;
  over.value = true;
  clock.stop();
  clearTimeout(advanceTimer);
  await finishRun({
    mode: 'last-gasp',
    score: score.value,
    lastedMs: ROUND_MS,
    correct: correct.value,
    wrong: wrong.value,
    bestChain: bestChain.value,
    ausBonuses: ausCount.value,
  });
  router.replace('/tabs/play/results');
}

// --- swipe ------------------------------------------------------------------
// Right is TRUE, left is FALSE. The threshold is generous because this is
// played at speed with a thumb, and a swipe that has to be precise is a swipe
// that gets read as a tap on whichever button was underneath it.
const SWIPE_MIN = 55;
let downX = null;
let downY = null;

function onDown(e) {
  downX = e.clientX;
  downY = e.clientY;
}

function onUp(e) {
  if (downX === null) return;
  const dx = e.clientX - downX;
  const dy = e.clientY - downY;
  downX = null;
  // Mostly horizontal, or it was a scroll.
  if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy)) return;
  answer(dx > 0);
}

function onCancel() {
  downX = null;
}

// Everything this screen has running, stopped. Idempotent: it is called on
// every leave, on abandon, and again before every re-entry.
function teardown() {
  over.value = true;
  clock.stop();
  clearTimeout(advanceTimer);
}

// Back to the pre-game screen. Without this, "Go again" returned to this same
// cached instance still holding the finished run.
function resetRun() {
  teardown();
  over.value = false;
  // Every ref back to the literal it was declared with — the state a
  // fresh mount would have. Resetting only the run flags left the LAST
  // question's verdict and its revealed answer painted over the new
  // board, so a new game opened showing why the previous one ended.
  started.value = false;
  counting.value = false;
  over.value = false;
  queue.value = [];
  idx.value = 0;
  revealed.value = false;
  chosen.value = null;
  shaking.value = false;
  ausNow.value = false;
  stageRef.value = null;
  score.value = 0;
  streak.value = 0;
  bestChain.value = 0;
  correct.value = 0;
  wrong.value = 0;
  ausCount.value = 0;
  verdict.value = { text: '', why: '', good: true };
}

useStopOnLeave(teardown);
useResetOnEnter(resetRun);

onUnmounted(() => {
  over.value = true;
  clearTimeout(advanceTimer);
});
</script>
